import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { MdManageAccounts } from 'react-icons/md';
import { propertiesOptions } from '../lib/constants';
import { isLocalDev } from '../lib/isLocalDev';
import { Profile, useProfile } from '../lib/profileContext';

type Persona = 'admin' | 'regular';

const PERSONAS: Record<Persona, Omit<Profile, 'property'>> = {
  admin: {
    firstName: 'Admin',
    lastName: 'Tester',
    email: 'admin.tester@example.com',
    employeeId: 'LOCAL-ADMIN',
    department: 'HR',
  },
  regular: {
    firstName: 'Team',
    lastName: 'Member',
    email: 'team.member@example.com',
    employeeId: 'LOCAL-MEMBER',
    department: 'Operations',
  },
};

function inferPersona(email: string): Persona {
  return email.toLowerCase() === PERSONAS.admin.email.toLowerCase()
    ? 'admin'
    : 'regular';
}

const DevUserSwitcher = () => {
  const { profile, updateProfile } = useProfile();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [persona, setPersona] = useState<Persona>('admin');
  const [property, setProperty] = useState('');

  const local = useMemo(() => isLocalDev(), []);

  // Keep form fields aligned with the active profile (avoid defaulting to CEG = sorted[0]).
  useEffect(() => {
    if (!profile.property && !profile.email) {
      return;
    }
    setPersona(inferPersona(profile.email || PERSONAS.admin.email));
    if (profile.property) {
      setProperty(profile.property);
    }
  }, [profile.email, profile.property]);

  if (!local) {
    return null;
  }

  const apply = () => {
    if (!property) {
      return;
    }
    const next: Profile = {
      ...PERSONAS[persona],
      property,
    };
    updateProfile(next);
    window.location.reload();
  };

  const reset = () => {
    localStorage.removeItem('profile');
    window.location.reload();
  };

  return (
    <Box position="fixed" bottom={4} right={4} zIndex={1400}>
      <Popover
        placement="top-end"
        closeOnBlur
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      >
        <PopoverTrigger>
          <IconButton
            aria-label="Local user switcher"
            icon={<MdManageAccounts size={22} />}
            colorScheme="orange"
            borderRadius="full"
            size="md"
            boxShadow="lg"
            title={`Local user switcher (${profile.property || 'no property'})`}
          />
        </PopoverTrigger>
        <PopoverContent
          width="320px"
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="xl"
          _focus={{ outline: 'none' }}
        >
          <PopoverArrow bg="white" />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="600" borderBottomWidth="1px">
            Local user switcher
          </PopoverHeader>
          <PopoverBody py={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  Active for API requests
                </Text>
                <HStack justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.700" noOfLines={1} flex={1} mr={2}>
                    {profile.email || '—'}
                  </Text>
                  <Badge colorScheme="purple" flexShrink={0}>
                    {profile.property || '—'}
                  </Badge>
                </HStack>
              </Box>

              <FormControl>
                <FormLabel fontSize="sm" mb={2}>
                  Persona
                </FormLabel>
                <RadioGroup
                  value={persona}
                  onChange={(v) => setPersona(v as Persona)}
                >
                  <Stack direction="row" spacing={5}>
                    <Radio value="admin" colorScheme="orange">
                      Admin
                    </Radio>
                    <Radio value="regular" colorScheme="orange">
                      Regular
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" mb={2}>
                  Property
                </FormLabel>
                <Select
                  size="sm"
                  bg="white"
                  placeholder="Select property"
                  value={property}
                  onChange={(e) => setProperty(e.target.value)}
                >
                  {propertiesOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <HStack justify="flex-end" pt={1} spacing={2}>
                <Button size="sm" variant="ghost" onClick={reset}>
                  Reset Okta
                </Button>
                <Button
                  size="sm"
                  colorScheme="orange"
                  onClick={apply}
                  isDisabled={!property}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default DevUserSwitcher;
